import {
  Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ContentChildren, TemplateRef, QueryList, ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { TableButton } from './table-button';
import { TableClickEvent } from './table-click-event';
import { TableColumn } from './table-column';
import { TableGroup } from './table-group';
import { CellTemplateRefDirective, CellTemplateContext } from './cell-template-ref.directive';
import { covertArrayToFlatFile, CSV_FORMAT } from '../flat-file-util';


  // tslint:disable: deprecation

/**
 * A component that displays a sortable table.  Use selector tcc-table.
 */
@Component({
  selector: 'tcc-table',
  template: `
<ng-content ></ng-content>
<table class="table dataTable table-striped table-bordered table-hover" [ngClass]="tableClass || ''">
  <thead>
    <tr *ngIf="visibleGroups.length" class="group-header">
      <th *ngFor="let grp of visibleGroups" [colSpan]="grp.length">
        {{grp.title}}
      </th>
      <th *ngIf="isButtonColumnVisible"></th>
    </tr>
    <tr>
      <th *ngFor="let col of visibleColumns" (click)="onHeaderClick(col.src)" [ngClass]="col.src.colClass || ''">
        <ng-container *ngIf="col.src.headerTemplateName; else NoHeaderTemplate">
          <ng-container *ngIf="cellTemplateRefsMap">
            <ng-container *ngTemplateOutlet="cellTemplateRefsMap.get(col.src.headerTemplateName) || null; context: { $implicit: col.src, col: col.src }"></ng-container>
          </ng-container>
        </ng-container>
        <ng-template #NoHeaderTemplate>
          {{col.title}}
        </ng-template>
        <tcc-sorter [sorterState]="col.src.sort || false"></tcc-sorter>
      </th>
      <th *ngIf="isButtonColumnVisible" class="button-col"></th>
    </tr>
  </thead>
  <tbody>
    <tr *ngFor="let row of rows; let i = index" [ngClass]="getRowCssClass(i) || ''">
      <ng-template ngFor let-col [ngForOf]="visibleColumns">
        <td *ngIf="isUsingCellTemplate(col.src); else NoCellTemplate"
            (click)="onCellClick(col.src, row, i)" [ngClass]="col.src.colClass || ''">
          <ng-container *ngIf="getCellTemplate(col.src)">
            <ng-container *ngTemplateOutlet="getCellTemplate(col.src);
                                             context: { $implicit: row, col: col.src, row: row }">
            </ng-container>
          </ng-container>
        </td>
        <ng-template #NoCellTemplate>
          <td (click)="onCellClick(col.src, row, i)" [ngClass]="col.src.colClass || ''" [innerHTML]="renderCellContent(col.src, row)"></td>
        </ng-template>
      </ng-template>
      <td *ngIf="isButtonColumnVisible" class="button-col">
        <span class="btn-group">
        <ng-template ngFor let-btn [ngForOf]="buttons">
          <ng-container *ngIf="!isButtonHidden(btn, row)">
            <button *ngIf="!btn.linkResolver" type="button" (click)="onButtonClick(btn, row)"
                    [ngClass]="btn.buttonClass || 'btn btn-xs btn-default'" [title]="btn.tooltip"
                    [disabled]="isButtonDisabled(btn, row)">
                <i [ngClass]="btn.iconClass || ''"></i> {{btn.text}}
            </button>
            <a *ngIf="btn.linkResolver"
                [href]="!isButtonDisabled(btn, row) ? btn.linkResolver(btn, row) : '#'"
                [target]="btn.openLinkInNewWindow ? '_blank' : ''"
                [class.disabled]="isButtonDisabled(btn, row)"
                [ngClass]="btn.buttonClass || 'btn btn-xs btn-default'" [title]="btn.tooltip">

                <i [ngClass]="btn.iconClass || ''"></i> {{btn.text}}
            </a>
          </ng-container>
        </ng-template>
        </span>
      </td>
    </tr>
  </tbody>
</table>
`,
  styles: [`
table { cursor: pointer; }
th { white-space: nowrap; }
.button-col { width:1px; white-space: nowrap; }
.group-header th { text-align: center; }
    `]
})
export class TableComponent implements AfterViewInit, OnChanges {
  private static readonly defaultSelectedRowClass = 'table-active';

  /** all templates transcluded into the table component */
  @ContentChildren(CellTemplateRefDirective)
  cellTemplateRefs!: QueryList<CellTemplateRefDirective>;
  /** templatesRefs converted to array after view init */
  cellTemplateRefsMap = new Map<string | number, TemplateRef<CellTemplateContext<any>>>();

  /** buttons that can appear at the end of a row */
  @Input() buttons?: TableButton<any>[];
  /** if true, client sort will be executed */
  @Input() enableClientSort = false;
  /** columns in a table.  To force an update send a new object as change detection only works if the object is different */
  @Input() columns?: TableColumn<any>[];
  /** groups that columns can be grouped into */
  @Input() groups?: TableGroup[];
  /** if true, when a cell is clicked a row is selectable */
  @Input() isSelectable = false;
  /** True if there is buttons and at least one is visible.  Does not take into account hidden resolver for row hiding.  */
  get isButtonColumnVisible() {
    return !!this.buttons?.some(x => !x.hidden);
  }
  /** passed to NgClass for each row */
  @Input() rowClass?: string | string[] | { [cssClass: string]: any };
  /** passed to NgClass for each row when selected */
  @Input() rowSelectedClass?: string | string[] | { [cssClass: string]: any };
  /** row data, can be any array of anything. */
  @Input() rows?: any[];

  private _selectedIndex: number | undefined;
  @Input()
  get selectedIndex() { return this._selectedIndex; }
  set selectedIndex(value: number | undefined) {
    this.updateSelectionByIndex(value);
  }

  private _rowMatchBy: ((row: any) => any) | undefined;
  /** can be either a string with property name or a function.
   * When selecting a row will use function to get key to match by (like trackByFn)
   */
  @Input()
  set rowMatchBy(value: string | number | ((row: any) => any) | undefined) {
    if (!value) {
      this._rowMatchBy = undefined;
    } else if (value instanceof Function) {
      this._rowMatchBy = value;
    } else {
      this._rowMatchBy = (x: any) => x[value];
    }
  }

  private _selectedRow: any;
  @Input()
  get selectedRow() { return this._selectedRow; }
  set selectedRow(value: any) {
    this.updateSelectionByRow(value);
  }

  /** passed to NgClass on the table */
  @Input() tableClass?: string | string[] | { [cssClass: string]: any };

  /** event emitted when any cell is clicked */
  @Output() cellClick = new EventEmitter<TableClickEvent<any>>();
  /** emitted when the selected index changes */
  @Output() selectedIndexChange = new EventEmitter<number>();
  /** emitted when the selected row changes */
  @Output() selectedRowChange = new EventEmitter<any>();
  /** event emitted when the table sort info has been changed.  Subscribe to this sorting and paging is done server side */
  @Output() sortChange = new EventEmitter<TableClickEvent<any>>();

  /** these are compiled columns that are currently visible */
  visibleColumns: CompiledColumn[] = [];
  /** these are compiled groups that are currently visible */
  visibleGroups: CompiledGroup[] = [];

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.cellTemplateRefs.filter(x => !!x.cellTemplateName)
      .forEach((x) => this.cellTemplateRefsMap.set(x.cellTemplateName!, x.templateRef));
    this.cd.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.columns) {
      this.updateVisibleColumns();
      this.applyClientSorting();
      this.updateSelectionByRow(this._selectedRow);
    }
    else if (changes.groups || changes.rows) {
      if (changes.groups) {
        this.updateVisibleColumns();
      }
      if (changes.rows) {
        this.applyClientSorting();
      }
      this.updateSelectionByRow(this._selectedRow);
    }
    if (changes.isSelectable) {
      if (!changes.isSelectable.currentValue) {
        this.updateSelectionByIndex(undefined);
      }
    }
  }

  onHeaderClick(col: TableColumn<any>) {
    this.toggleSort(col);
  }

  onButtonClick(button: TableButton<any>, row: any) {
    this.cellClick.emit({ button, row });
  }

  onCellClick(col: TableColumn<any>, row: any, index: number) {
    if (this.isSelectable) {
      this.updateSelectionByRow(row);
    }
    this.cellClick.emit({ column: col, row });
  }

  /**
   * Will export data from the table into a csv string (You can use FileDownload.service to export data)
   * @deprecated This will be removed.  Use covertArrayToFlatFile
   */
  exportCsv(customColumns?: TableColumn<any>[], customGroups?: TableGroup[]) {
    const compiled = this.compileHeadings(customColumns || this.columns, customGroups || this.groups);
    return this.tableToCsvText(compiled.groups, compiled.columns, this.rows);
  }

  getRowCssClass(rowIndex: number) {
    if (rowIndex !== this.selectedIndex) {
      return this.rowClass;
    }
    /** Converts a cssClass variable to an array of classes */
    function normalizeCssClass(cssClass: string | string[] | { [cssClass: string]: any } | undefined) {
      if (!cssClass) { return []; }
      if (Array.isArray(cssClass)) { return cssClass; }
      if (typeof (cssClass) === 'string') { return [cssClass]; }
      const classes: string[] = [];
      for (const key in cssClass) {
        if (cssClass[key]) {
          classes.push(key);
        }
      }
      return classes;
    }
    return normalizeCssClass(this.rowClass)
      .concat(normalizeCssClass(this.rowSelectedClass ?? TableComponent.defaultSelectedRowClass));
  }

  /** Returns cell template for column if it exists in cellTemplateRefsMap, which isn't populated until AfterViewInit. */
  getCellTemplate(col: TableColumn<any>) {
    return (this.cellTemplateRefsMap.has(col.cellTemplateName ?? col.name))
      ? this.cellTemplateRefsMap.get(col.cellTemplateName ?? col.name) || null
      : null;
  }
  /** returns true if the column should use a cell template. */
  isUsingCellTemplate(col: TableColumn<any>)  {
    return col.cellTemplateName != null || this.cellTemplateRefsMap.has(col.name);
  }
  /**
   * returns true if button is diabled for row
   */
  isButtonDisabled(button: TableButton<any>, row: any) {
    return (button.disabled || (button.disabledResolver && button.disabledResolver(row)))
  }

  /**
   * returns true if button is hidden for row
   */
  isButtonHidden(button: TableButton<any>, row: any) {
    return (button.hidden || (button.hiddenResolver && button.hiddenResolver(row)))
  }


  /**
   * renders the content for within a cell for a non-template column.
   */
  renderCellContent(col: TableColumn<any>, row: any) {
    const content = (!col.contentResolver) ? row[col.name] : col.contentResolver(col, row);
    if (col.linkResolver) {
      const link = col.linkResolver(content, col, row);
      const targetAttr = (col.openLinkInNewWindow) ? 'target="_blank"' : '';
      return `<a href="${encodeURI(link)}" ${targetAttr}>${content}</a>`;
    }
    return content;
  }

  /** Toggles sort for a column if it is sortable, otherwise does nothing. */
  toggleSort(col: TableColumn<any>) {
    if (this.columns && col && col.sort) {
      // reset sort columns
      for (const otherCol of this.columns) {
        if (otherCol.name !== col.name && otherCol.sort) otherCol.sort = true;
      }
      col.sort = (col.sort === 'asc') ? 'desc' : 'asc';
      if (this.applyClientSorting(col)) {
        this.updateSelectionByRow(this._selectedRow);
      }
      this.sortChange.emit({ column: col });
    }
  }

  /**
   * Creates Text file string from table data
   * @param groups Groups headers that may be displayed on top of columns
   * @param columns Column headers with optional alternative title
   * @param rows Row Headers
   */
  private tableToCsvText(groups: { length: number, title: any }[],
    columns: { src: TableColumn<any>, title?: any }[],
    rows: any[] | undefined) {

    const cells: any[][] = [];

    if (groups && groups.length > 0) {
      // add group headings
      const groupRow = groups.reduce((acc, cur) => {
        acc.push(cur.title);
        acc.length += cur.length - 1;
        return acc;
      }, [] as any[]);
      cells.push(groupRow);
    }
    // add col headings
    cells.push(columns.map(x => x.title || x.src.title || x.src.name));
    rows?.forEach(r => cells.push(columns.map(c => c.src.contentResolver ? c.src.contentResolver(c.src, r) : r[c.src.name])));

    return covertArrayToFlatFile(cells, CSV_FORMAT);
  }
  /** Tries to finid the row and then runs updateSelectionByIndex. */
  private updateSelectionByRow(row: any) {
    let foundIndex: number = -1;
    if (this.rows && this.rows.length > 0 && row !== undefined && this.isSelectable) {
      if (this._rowMatchBy) {
        const find = this._rowMatchBy(row);
        for (let i = 0, il = this.rows.length; i < il; i++) {
          const compareTo = this.rows[i];
          // tslint:disable-next-line: triple-equals
          if (compareTo != null && find == this._rowMatchBy(compareTo)) {
            foundIndex = i;
            break;
          }
        }
      } else {
        foundIndex = this.rows.indexOf(row);
      }
    }
    this.updateSelectionByIndex(foundIndex);
  }
  /**
   * sets the current selection by index, emitting any events if changes occur.
   * @param index the index to set the selection to
   */
  private updateSelectionByIndex(index: number | undefined) {
    let changes: { index: number | undefined, row: any };
    if (index != null && this.rows && index >= 0 && index < this.rows.length && this.isSelectable) {
      changes = { index, row: this.rows[index] };
    } else {
      changes = { index: undefined, row: undefined };
    }
    const indexChanged = changes.index !== this._selectedIndex;
    const rowChanged = changes.row !== this._selectedRow;

    if (indexChanged || rowChanged) {
      this._selectedIndex = changes.index;
      this._selectedRow = changes.row;
      if (indexChanged) setTimeout(() => this.selectedIndexChange.emit(changes.index), 1);
      if (rowChanged) setTimeout(() => this.selectedRowChange.emit(changes.row), 1);
    }
  }
  /**
   * apply client side sorting if applicable
   * @param col the column to sort, if not passed will search for the currently selected sorted column.
   * @returns true if executed sort function;
   */
  private applyClientSorting(col?: TableColumn<any>): boolean {
    if (!this.enableClientSort || !this.rows || this.rows.length === 0 || !this.columns || this.columns.length === 0) {
      return false; // nothing to sort
    }

    if (!col) {
      for (const tryCol of this.columns) {
        if (tryCol.sort === 'desc' || tryCol.sort === 'asc') {
          col = tryCol;
          break;
        }
      }
    }
    if (col?.sorter) {
      // create local instance of sorter because it could be made undefined wher sortFunc is called.
      const sorter = col.sorter;
      // reverse row order if descending
      const sortFunc = (col.sort === 'desc') ? (rowA: any, rowB: any) => sorter(rowB, rowA) : sorter;

      this.rows.sort(sortFunc);
      return true;
    }
    // if there is no sort function the sorting is not implemented on the client side.
    return false;
  }

  /**
   * sets visible columns from columns
   */
  private updateVisibleColumns() {
    const compiled = this.compileHeadings(this.columns, this.groups);
    this.visibleGroups = compiled.groups;
    this.visibleColumns = compiled.columns;
  }

  /**
   * Converts groups and columns to format for outputting
   * @param groups Groups to Output
   * @param columns Columns to Ouput
   */
  private compileHeadings(columns: TableColumn<any>[] | undefined, groups: TableGroup[] | undefined) {
    const output: { groups: CompiledGroup[], columns: CompiledColumn[] } = {
      groups: [],
      columns: []
    };

    if (!columns || columns.length === 0) {
      return output;
    }

    if (!groups || groups.length === 0) {
      // if no groups then rending columns is just adding visible columns to output
      for (const col of columns) {
        if (!col.hidden) {
          output.columns.push({ src: col, title: col.title || col.name });
        }
      }
    }
    else {
      // columns are organized by the position of the group and then their original position
      // a default group for unmatched columns is created, those are moved to the front.
      const groupSrc: { src: TableGroup, cols: TableColumn<any>[] }[] = [];
      const groupIndex: string[] = [];

      for (const grp of [<TableGroup>{ name: '$$UNMATCHED$$', title: '' }].concat(groups)) {
        groupSrc.push({ src: grp, cols: [] });
        groupIndex.push(grp.name);
      }
      for (const col of columns) {
        if (!col.hidden) {
          const grpIndex = col.groupName ? groupIndex.indexOf(col.groupName) : -1;
          groupSrc[(grpIndex === -1) ? 0 : grpIndex].cols.push(col);
        }
      }
      for (let i = 0, il = groupSrc.length; i < il; i++) {
        // output each group IF it has columns assigned
        const groupInfo = groupSrc[i];
        if (groupInfo.cols.length > 0) {
          output.groups.push({ length: groupInfo.cols.length, title: getTitle(groupInfo.src) });
          output.columns.push(...groupInfo.cols.map(col => ({ src: col, title: getTitle(col) })));
        }
      }
    }

    return output;

    function getTitle(item: { title?: string, name: string | number }) {
      if (item) {
        return (item.title != null) ? item.title : item.name;
      }
      return undefined;
    }
  }
}

export interface CompiledGroup {
  /** number of columns the group takes up */
  length: number;
  /** rendered title for the group */
  title: any;
}

export interface CompiledColumn {
  /** source data for the rendered column */
  src: TableColumn<any>;
  /** rendered title for the column */
  title: any;
}
